/**
 * Worker shift-report sign screen — §3.6.
 *
 * Третий экран worker-flow: сводка отчёта (group by project — «manifest»),
 * редактируемый табель (HoursStepper), три info-баннера по соотношению
 * `sum_entries` ↔ `declared` ↔ `shift_today`, sticky-bottom «Подписать»,
 * PinSheet (§3.2) на тап.
 *
 * Поток:
 *   GET /field/timesheet?date=<active>     — переиспользуем cache из §3.4
 *   POST /field/timesheet/sign?date=<>     — { pin, declared_total_hours_half }
 *
 * Архитектурные решения (assessment §3.6):
 *   - `declared_total_hours_half` шлём ВСЕГДА (seed = response.default_*).
 *     Иначе UI без явного значения подпишет 0 = «не работал на смене».
 *   - На выходной `shift_today_half = 16` fallback приходит с бэка — UI ничего
 *     не блокирует (любая `declared > 0` на выходной = переработка, ок).
 *   - 423 в PinSheet → CTA «Закрыть» возвращает на /field, НЕ на /login.
 *     Иначе cookie живая, при следующем заходе снова lockout.
 *
 * Дата берётся из URL ?date=YYYY-MM-DD (если §3.4 переходит на backfill);
 * без параметра — серверная активная (today). Query-key `['timesheet', date]`
 * совпадает с §3.4, поэтому cache hot из main-экрана и наоборот.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ChevronLeft, Loader2, Pencil } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ru as ruLocale, enUS } from "date-fns/locale";
import { toast } from "sonner";

import { FieldShell } from "@/layout/FieldShell";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { useMe } from "@/hooks/useMe";
import type { ApiError } from "@/api/client";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { presentApiError } from "@/lib/api-error-policy";
import { getFieldTimesheet, signFieldTimesheet } from "@/api/field-timesheet";
import type { FieldTimesheetResponse } from "@/api/field-timesheet";
import { TabelEditorSheet } from "@/components/field/TabelEditorSheet";
import {
  buildProjectMap,
  buildWorkTypeCatalogs,
  fmtH,
  groupEntriesByProject,
  type ProjectGroup,
} from "@/lib/field-summary";
import { PinSheet } from "@/components/field/PinSheet";
import type { PinSheetState } from "@/components/field/PinSheet";
import { SignConfirmSheet } from "@/components/field/SignConfirmSheet";
import { OvertimeBanner } from "@/components/timesheet/OvertimeBanner";
import { drainQueue, enqueue, hasPendingEntries, isNetworkError } from "@/lib/field-offline-queue";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LOCALES = { ru: ruLocale, en: enUS } as const;

function resolveLocale(locale?: string): (typeof LOCALES)["ru"] {
  const key = (locale ?? "ru").slice(0, 2) as keyof typeof LOCALES;
  return LOCALES[key] ?? LOCALES.ru;
}

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function hasErrorCode(value: unknown, key: "code" | "error", code: string): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    key in value &&
    (value as Record<string, unknown>)[key] === code
  );
}

/**
 * Search-param для возврата на `/field`: если активная дата = сегодня,
 * URL чистим; иначе сохраняем `?date=YYYY-MM-DD`, чтобы FieldTimesheetPage
 * подхватил backfill-день после подписи.
 */
function fieldSearchForDate(activeDate: string): { date?: string } {
  return activeDate === todayIso() ? {} : { date: activeDate };
}

function readDateFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("date");
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

function formatChip(iso: string, locale: string): string {
  // §20/7.1: двухбуквенные дни недели (EEEEEE → пн/вт/ср) вместо трёхбуквенных
  // «срд», которые обрезались в чипе на узких экранах.
  return format(parseISO(`${iso}T00:00:00`), "dd.MM EEEEEE", {
    locale: resolveLocale(locale),
  });
}

// Юнит для declarative-строки. Вынесен в константу, чтобы eslint
// `i18next/no-literal-string` не считал её JSX-литералом (как и `fmtH`
// в field-summary, мы намеренно держим единицу одной строкой).
const HOURS_UNIT_LABEL = "ч";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FieldSignPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { user } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Дата read-only из URL `?date=YYYY-MM-DD` (backfill из §3.4 при необходимости).
  // §3.4 пока навигирует без даты — fallback на today.
  const activeDate = readDateFromUrl() ?? todayIso();

  // Тот же query-key, что в §3.4 — переиспользуем cache.
  const query = useQuery<FieldTimesheetResponse, ApiError>({
    queryKey: ["timesheet", activeDate] as const,
    queryFn: () => getFieldTimesheet(activeDate),
    retry: false,
    staleTime: 0,
  });

  // User-override declared. Хранится только когда пользователь явно тронул
  // stepper; иначе всегда читаем `default_declared_hours_half` из ответа.
  // Ключ overrides — `activeDate`, чтобы переход backfill→today сбрасывал
  // ручное значение и подхватывал свежий default (например 7ч на сокращённый
  // день). Так избегаем set-state-in-effect и ref-в-render одновременно.
  const [overrideByDate, setOverrideByDate] = React.useState<Record<string, number>>({});
  const userOverride = overrideByDate[activeDate];
  const defaultDeclared = query.data?.default_declared_hours_half ?? null;
  const declaredHalf = userOverride !== undefined ? userOverride : (defaultDeclared ?? null);
  useReleaseDirtyState(userOverride !== undefined && userOverride !== defaultDeclared);
  const setDeclaredHalf = React.useCallback(
    (next: number) => {
      setOverrideByDate((prev) => ({ ...prev, [activeDate]: next }));
    },
    [activeDate],
  );

  // Bottom-sheet редактор табеля (§4.3).
  const [tabelSheetOpen, setTabelSheetOpen] = React.useState(false);

  // Способ подписи ветвится по роли (§73/2.2): линейный (`worker`) — PIN-пад,
  // офисный — доверенная сессия без PIN (лёгкое подтверждение). Роль берём из
  // `/me` (не из `data.me`, там роли нет). До загрузки `user` — трактуем как
  // офисного (PIN-пад не покажем «вхолостую»); сессия/роут уже гарантируют
  // залогиненного к моменту тапа «Подписать».
  const isWorker = user?.role === "worker";

  // Sign-sheet: PinSheet у линейного / SignConfirmSheet у офисного. Один флаг
  // «открыта панель подписи» на обе ветки.
  const [signSheetOpen, setSignSheetOpen] = React.useState(false);
  const [pinState, setPinState] = React.useState<PinSheetState>({ kind: "idle" });
  // Счётчик попыток ведём в UI: backend на 401 не возвращает «осталось N».
  // Синхронен общему `failed_pin_attempts` (max 3) из §1.4 PIN helper.
  const attemptsRef = React.useRef(0);

  const signMutation = useMutation({
    mutationFn: async (pin: string | null) => {
      // Assessment §3.9: если в очереди ещё висят timesheet-мутации — ждём
      // их завершения и invalidate'им cache, чтобы backend увидел свежий
      // sum_entries при подписи. Без этого можно подписать declared над
      // неполным локальным состоянием. drainQueue сам invalidate'нёт после
      // успешного применения каждой записи. Если drain не смог дойти до
      // конца (network), всё равно пробуем sign — backend подпишет то, что
      // у него реально лежит.
      if (user && hasPendingEntries(user.id)) {
        await drainQueue(user.id, queryClient, {
          onDropped: () => toast.error(t("field.offline.toast.dropped")),
        });
      }
      return signFieldTimesheet({ pin, declared_total_hours_half: declaredHalf ?? 0 }, activeDate);
    },
    onMutate: () => {
      // PinSheet-state двигаем только у линейного; у офисного submit-состояние
      // читает SignConfirmSheet из `signMutation.isPending`.
      if (isWorker) setPinState({ kind: "submitting" });
    },
    onSuccess: () => {
      attemptsRef.current = 0;
      setPinState({ kind: "success" });
      setSignSheetOpen(false);
      // Cache отчёта мог поменяться (status=signed) — обнови, чтобы §3.4
      // увидел свежее состояние при возврате.
      void queryClient.invalidateQueries({ queryKey: ["timesheet", activeDate] });
      toast.success(t("field.sign.toast.submitted"));
      void navigate({ to: "/field", search: fieldSearchForDate(activeDate) });
    },
    onError: (err: ApiError | Error, pin: string | null) => {
      // Network-error → persistent queue. На сервер подпись прилетит после
      // reconnect'а; пользователь возвращается на /field и видит «отправили».
      // PIN кладём в queue в открытом виде — это вынужденная цена offline-
      // подписи; localStorage в Android Chrome изолирован per-origin, и
      // alternativa (повторный prompt после reconnect'а) ломает UX. У офисного
      // `pin` = null (доверенная сессия) — replay уходит без PIN.
      if (isNetworkError(err) && user) {
        enqueue(user.id, {
          kind: "sign",
          method: "POST",
          path: `/field/timesheet/sign?date=${encodeURIComponent(activeDate)}`,
          body: { pin, declared_total_hours_half: declaredHalf ?? 0 },
          date: activeDate,
        });
        attemptsRef.current = 0;
        setPinState({ kind: "idle" });
        setSignSheetOpen(false);
        toast.success(t("field.offline.toast.savedLocally"));
        void navigate({ to: "/field", search: fieldSearchForDate(activeDate) });
        return;
      }
      const presentation = presentApiError(err, {
        fallbackMessage: t("field.sign.toast.failed"),
        domainRules: [
          {
            code: "invalid_pin",
            status: 401,
            detail: "invalid_pin",
            message: t("field.pin.error.attempt", {
              attempt: attemptsRef.current + 1,
              max: 3,
            }),
          },
          {
            code: "account_locked",
            status: 423,
            matches: (detail) => hasErrorCode(detail, "code", "account_locked"),
            message: t("field.pin.lockout.message"),
          },
          {
            code: "already_approved",
            status: 409,
            detail: "отчёт уже подтверждён администратором",
            message: t("field.sign.toast.alreadyApproved"),
          },
          {
            code: "backfill_window_exceeded",
            status: 410,
            matches: (detail) => hasErrorCode(detail, "error", "backfill_window_exceeded"),
            message: t("field.sign.toast.backfillExceeded"),
          },
        ],
      });
      if (presentation.kind === "domain" && presentation.code === "invalid_pin") {
        attemptsRef.current += 1;
        setPinState({
          kind: "error",
          attempts: attemptsRef.current,
          maxAttempts: 3,
        });
        return;
      }
      if (presentation.kind === "domain" && presentation.code === "account_locked") {
        setPinState({ kind: "lockout" });
        return;
      }
      setSignSheetOpen(false);
      setPinState({ kind: "idle" });
      attemptsRef.current = 0;
      if (presentation.kind === "domain" && presentation.code === "already_approved") {
        toast.error(presentation.message);
        void navigate({ to: "/field", search: fieldSearchForDate(activeDate) });
        return;
      }
      if (presentation.kind === "domain" && presentation.code === "backfill_window_exceeded") {
        toast.error(presentation.message);
        void navigate({ to: "/field" });
        return;
      }
      showApiErrorToast(err, { fallbackMessage: t("field.sign.toast.failed") });
    },
  });

  // 410 на GET (теоретически не должно — пришли с §3.4) — defensive redirect.
  if (
    query.error?.status === 410 &&
    hasErrorCode(query.error.body, "error", "backfill_window_exceeded")
  ) {
    void navigate({ to: "/field" });
    return (
      <FieldShell context={t("field.shell.context.sign")}>
        <div className="flex h-full items-center justify-center" />
      </FieldShell>
    );
  }

  if (query.isError) {
    return (
      <FieldShell context={t("field.shell.context.sign")}>
        <div className="flex h-full items-center justify-center px-4 text-center">
          <ApiErrorNotice error={query.error} fallbackMessage={t("common.apiError.fallback")} />
        </div>
      </FieldShell>
    );
  }

  if (query.isPending || !query.data || declaredHalf === null) {
    return (
      <FieldShell context={t("field.shell.context.sign")}>
        <div className="flex h-full items-center justify-center" data-testid="field-sign-loading">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--fg-3)" }} aria-hidden />
        </div>
      </FieldShell>
    );
  }

  const data = query.data;
  const sumHalf = data.entries.reduce((acc, e) => acc + e.hours_half, 0);
  // `shift_today_half` приходит с бэка как `default_declared_hours_half`. На
  // выходной = 16 fallback. Норма дня для `OvertimeBanner` (declared vs norm)
  // и `TabelEditorSheet` (overtime detection).
  const shiftTodayHalf = data.default_declared_hours_half;

  const projectsById = buildProjectMap(data.my_recent_projects);
  const { workTypeNameById } = buildWorkTypeCatalogs(data.my_work_types);
  const groups = groupEntriesByProject(data.entries, projectsById, workTypeNameById);

  function handlePinSubmit(pin: string): void {
    signMutation.mutate(pin);
  }

  function handlePinClose(open: boolean): void {
    if (open) return;
    if (pinState.kind === "lockout") {
      // Lockout-CTA закрывает sheet → возвращаемся на /field (assessment §3.6).
      void navigate({ to: "/field", search: fieldSearchForDate(activeDate) });
    }
    setSignSheetOpen(false);
    setPinState({ kind: "idle" });
  }

  // Офисный (§73/2.2): подтверждение без PIN — доверенная сессия.
  function handleOfficeConfirm(): void {
    signMutation.mutate(null);
  }

  function handleOfficeClose(open: boolean): void {
    // Пока идёт submit — не даём закрыть панель (висящий fetch).
    if (!open && signMutation.isPending) return;
    setSignSheetOpen(open);
  }

  return (
    <FieldShell context={t("field.shell.context.sign")}>
      {/* Header strip — ФИО + дата (read-only chip, без date-picker) */}
      <ProfileStrip
        name={user?.display_name ?? ""}
        workRole={data.me.work_role ?? null}
        activeDate={activeDate}
        locale={i18n.language}
        onBack={() => {
          void navigate({ to: "/field", search: fieldSearchForDate(activeDate) });
        }}
      />

      {/* Scrollable body — sticky-bar bottom-padding compute (как §3.4) */}
      <div
        className="flex flex-col"
        data-testid="field-sign-body"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
      >
        {/* SECTION 1 — Manifest summary */}
        <SummarySection
          groups={groups}
          totalHalf={sumHalf}
          labels={{
            title: t("field.sign.summary.title"),
            total: t("field.sign.summary.total"),
            empty: t("field.sign.summary.empty"),
          }}
        />

        {/* SECTION 2 — Tabel value + overtime banner. Редактирование — bottom-sheet. */}
        <DeclaredSection
          declaredHalf={declaredHalf}
          onEditRequest={() => setTabelSheetOpen(true)}
          shiftTodayHalf={shiftTodayHalf}
        />
      </div>

      <TabelEditorSheet
        open={tabelSheetOpen}
        onOpenChange={setTabelSheetOpen}
        declaredHalf={declaredHalf}
        shiftTodayHalf={shiftTodayHalf}
        onApply={setDeclaredHalf}
      />

      {/* Sticky bottom — «Подписать» */}
      <SignBar
        label={t("field.sign.button")}
        onPress={() => {
          attemptsRef.current = 0;
          setPinState({ kind: "idle" });
          setSignSheetOpen(true);
        }}
      />

      {/* Подпись ветвится по роли (§73/2.2): линейный — PIN-пад, офисный —
          подтверждение без PIN (доверенная сессия). */}
      {isWorker ? (
        <PinSheet
          open={signSheetOpen}
          onOpenChange={handlePinClose}
          title={t("field.pin.title")}
          description={t("field.sign.pinTitle")}
          state={pinState}
          onSubmit={handlePinSubmit}
          data-testid="field-sign-pin"
        />
      ) : (
        <SignConfirmSheet
          open={signSheetOpen}
          onOpenChange={handleOfficeClose}
          name={user?.display_name ?? data.me.full_name}
          submitting={signMutation.isPending}
          onConfirm={handleOfficeConfirm}
          data-testid="field-sign-confirm"
        />
      )}
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------
// ProfileStrip — упрощённая версия из §3.4 (без date-picker)
// ---------------------------------------------------------------------------

export interface ProfileStripProps {
  name: string;
  workRole: string | null;
  activeDate: string;
  locale: string;
  onBack: () => void;
}

export function ProfileStrip({
  name,
  workRole,
  activeDate,
  locale,
  onBack,
}: ProfileStripProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-2 px-4 pt-4 pb-3"
      style={{ borderBottom: "1px solid var(--rule-line)" }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t("field.sign.back.aria")}
        data-testid="field-sign-back"
        className="-ml-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-button)] transition-colors hover:bg-[var(--bg-panel-2)]"
        style={{ color: "var(--fg-2)" }}
      >
        <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
      </button>
      <div className="min-w-0 flex-1">
        <div
          className="truncate font-condensed text-[20px] font-semibold uppercase leading-tight tracking-[0.04em]"
          style={{ color: "var(--fg-1)" }}
          data-testid="field-sign-profile-name"
        >
          {name || "—"}
        </div>
        {workRole && (
          <div
            className="mt-0.5 truncate font-condensed text-[11px] uppercase tracking-[0.18em]"
            style={{ color: "var(--fg-3)" }}
          >
            {workRole}
          </div>
        )}
      </div>
      <div
        className="inline-flex h-11 items-center px-3 font-condensed text-[13px] uppercase tracking-[0.08em]"
        style={{
          color: "var(--fg-2)",
          borderRadius: "var(--radius-button)",
          border: "1px solid var(--rule-line)",
          background: "var(--bg-panel-0)",
          fontVariantNumeric: "tabular-nums",
        }}
        data-testid="field-sign-date"
      >
        {formatChip(activeDate, locale)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SummarySection — manifest с группировкой по проекту
// ---------------------------------------------------------------------------

interface SummarySectionProps {
  groups: readonly ProjectGroup[];
  totalHalf: number;
  labels: { title: string; total: string; empty: string };
}

function SummarySection({ groups, totalHalf, labels }: SummarySectionProps): React.ReactElement {
  return (
    <section data-testid="field-sign-summary" className="px-4 pt-5 pb-6">
      <SectionHeader title={labels.title} />

      {groups.length === 0 ? (
        <div
          className="mt-4 px-1 py-3 font-condensed text-[12px] uppercase leading-snug tracking-[0.1em]"
          style={{ color: "var(--fg-3)" }}
          data-testid="field-sign-summary-empty"
        >
          {labels.empty}
        </div>
      ) : (
        <ul
          className="mt-3 flex flex-col"
          style={{ listStyle: "none", padding: 0 }}
          data-testid="field-sign-summary-list"
        >
          {groups.map((g) => (
            <ProjectGroupRow key={g.projectKey} group={g} />
          ))}
        </ul>
      )}

      {/* Total — typographic «брусок» в правом нижнем углу секции */}
      <div
        className="mt-4 flex items-baseline justify-between border-t pt-3"
        style={{ borderColor: "var(--rule-line)" }}
        data-testid="field-sign-summary-total"
      >
        <span
          className="font-condensed text-[10px] uppercase tracking-[0.22em]"
          style={{ color: "var(--fg-3)" }}
        >
          {labels.total}
        </span>
        <span
          className="font-display text-[26px] font-semibold leading-none tabular-nums"
          style={{
            color: "var(--fg-1)",
            fontFamily: "var(--font-display)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtH(totalHalf)}
        </span>
      </div>
    </section>
  );
}

function ProjectGroupRow({ group }: { group: ProjectGroup }): React.ReactElement {
  return (
    <li
      className="flex flex-col py-2"
      style={{ borderTop: "1px solid var(--rule-line)" }}
      data-testid="field-sign-project-group"
      data-project={group.projectKey}
    >
      {/* Заголовок группы — крупный код проекта + сумма часов */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="truncate font-condensed text-[16px] font-semibold uppercase leading-tight tracking-[0.04em]"
            style={{ color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}
          >
            {group.code}
            {group.isRaw && (
              <AlertTriangle
                className="ml-1 inline-block h-3.5 w-3.5 align-[-1px]"
                style={{ color: "#b45309" }}
                aria-hidden
              />
            )}
            {group.name && (
              <>
                <span style={{ color: "var(--fg-3)" }}> · </span>
                <span
                  className="font-condensed text-[15px] font-normal uppercase tracking-[0.04em]"
                  style={{ color: "var(--fg-2)" }}
                >
                  {group.name}
                </span>
              </>
            )}
          </div>
        </div>
        <div
          className="shrink-0 font-display text-[18px] font-semibold tabular-nums"
          style={{
            color: "var(--fg-1)",
            fontFamily: "var(--font-display)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtH(group.totalHalf)}
        </div>
      </div>

      {/* Подстроки — каждая запись work_type · ч */}
      <ul className="mt-1 ml-0 flex flex-col gap-0.5" style={{ listStyle: "none", padding: 0 }}>
        {group.rows.map((r) => (
          <li
            key={r.id}
            className="flex items-baseline gap-2 text-[12px] leading-snug"
            data-testid="field-sign-summary-row"
          >
            <Check
              aria-hidden
              className="h-3 w-3 shrink-0"
              style={{ color: "var(--accent-agent)" }}
            />
            <span className="min-w-0 flex-1 truncate" style={{ color: "var(--fg-2)" }}>
              {r.workType ?? "—"}
            </span>
            <span
              className="shrink-0 tabular-nums"
              style={{
                color: "var(--fg-2)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtH(r.hoursHalf)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}

function SectionHeader({ title }: { title: string }): React.ReactElement {
  return (
    <h2
      className="font-condensed text-[11px] font-semibold uppercase tracking-[0.22em]"
      style={{ color: "var(--fg-2)", fontFamily: "var(--font-display)" }}
    >
      {title}
    </h2>
  );
}

// ---------------------------------------------------------------------------
// DeclaredSection — value-блок табеля + footer-плашка (§4.3)
// ---------------------------------------------------------------------------

interface DeclaredSectionProps {
  declaredHalf: number;
  shiftTodayHalf: number;
  onEditRequest: () => void;
}

/**
 * Value-блок «Табель / в табель пойдёт N ч ✎ изменить» — без collapse. Тап
 * по «изменить» открывает `TabelEditorSheet` (bottom-sheet) у родителя.
 * Под значением — общий `OvertimeBanner` (`declared` vs производственная норма).
 */
export function DeclaredSection({
  declaredHalf,
  shiftTodayHalf,
  onEditRequest,
}: DeclaredSectionProps): React.ReactElement {
  const { t } = useTranslation();
  const declaredValue = declaredHalf / 2;
  const declaredDisplay = Number.isInteger(declaredValue)
    ? `${declaredValue}`
    : declaredValue.toFixed(1);

  return (
    <section
      className="px-4 pt-2 pb-6"
      data-testid="field-sign-declared"
      style={{ borderTop: "1px solid var(--rule-line)" }}
    >
      <div className="pt-5">
        <SectionHeader title={t("field.sign.declared.title")} />
      </div>

      <div
        className="mt-3 flex items-end justify-between gap-3"
        data-testid="field-sign-declared-statement"
      >
        <div className="flex min-w-0 flex-col">
          <span
            className="font-display font-semibold leading-none tabular-nums"
            style={{
              color: "var(--fg-1)",
              fontFamily: "var(--font-display)",
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
              fontSize: "var(--fs-40, 40px)",
              letterSpacing: "0.01em",
            }}
            data-testid="field-sign-declared-value"
          >
            {declaredDisplay}
            <span
              className="ml-1.5 align-baseline font-condensed text-[14px] font-normal uppercase tracking-[0.18em]"
              style={{ color: "var(--fg-3)" }}
            >
              {HOURS_UNIT_LABEL}
            </span>
          </span>
        </div>

        <button
          type="button"
          onClick={onEditRequest}
          data-testid="field-sign-declared-edit"
          className="inline-flex shrink-0 items-center gap-1.5 self-end pb-1 font-condensed text-[11px] uppercase tracking-[0.18em] transition-colors hover:opacity-70"
          style={{
            color: "var(--fg-2)",
            fontFamily: "var(--font-display)",
            background: "transparent",
            border: 0,
            padding: 0,
          }}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
          <span
            style={{
              borderBottom: "1px solid var(--rule-line)",
              paddingBottom: 1,
            }}
          >
            {t("field.sign.declared.edit")}
          </span>
        </button>
      </div>

      <div className="mt-4">
        <OvertimeBanner
          normHoursHalf={shiftTodayHalf}
          actualHoursHalf={declaredHalf}
          data-testid="field-sign-footer"
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sticky sign bar — симметричен §3.4
// ---------------------------------------------------------------------------

interface SignBarProps {
  label: string;
  onPress: () => void;
}

function SignBar({ label, onPress }: SignBarProps): React.ReactElement {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-20 flex items-center gap-3 px-4 pt-3"
      style={{
        background: "var(--bg-panel-0)",
        borderTop: "1px solid var(--rule-line)",
        boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.08)",
        paddingBottom: "max(8px, env(safe-area-inset-bottom, 8px))",
      }}
      data-testid="field-sign-sticky-bar"
    >
      <button
        type="button"
        onClick={onPress}
        className="inline-flex h-12 flex-1 items-center justify-center gap-2 px-5 font-condensed text-[14px] font-semibold uppercase tracking-[0.12em] transition-colors"
        style={{
          color: "var(--fg-inverse)",
          background: "var(--soot-900, #16181b)",
          borderRadius: "var(--radius-button)",
        }}
        data-testid="field-sign-submit"
      >
        {label} →
      </button>
    </div>
  );
}
